import React, { useState, useEffect } from "react";
import API from "../services/api";
import { formatScholarId } from "../utils/idFormatter";
import ProfileModal from "./ProfileModal";
import { getUserProfile, getScholarProfile } from "../services/adminService";

interface BookingDetailsModalProps {
  open: boolean;
  booking: any | null;
  onClose: () => void;
  onRefundClick?: (booking: any) => void;
  onReimburseClick?: (booking: any) => void;
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 10000,
  background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 16,
};

const modal: React.CSSProperties = {
  background: "hsl(var(--card))", borderRadius: 20,
  width: "min(96vw, 720px)", maxHeight: "92vh",
  display: "flex", flexDirection: "column",
  boxShadow: "0 25px 60px rgba(0,0,0,0.22)",
  animation: "bdmSlideUp .25s ease",
  border: "1px solid hsl(var(--border))",
};

const pill: React.CSSProperties = {
  display: "inline-block", padding: "3px 10px", borderRadius: 20,
  fontSize: 11, fontWeight: 700,
};

const STATUS_MAP: Record<string, { bg: string; color: string }> = {
  ACCEPTED:    { bg: "#f0fdf4", color: "#15803d" },
  COMPLETED:   { bg: "#f0fdf4", color: "#15803d" },
  PENDING:     { bg: "#fffbeb", color: "#b45309" },
  IN_PROGRESS: { bg: "#eff6ff", color: "#1e40af" },
  CANCELLED:   { bg: "#fef2f2", color: "#b91c1c" },
  DISPUTED:    { bg: "#fff1f2", color: "#9f1239" },
  DECLINED:    { bg: "#fef2f2", color: "#b91c1c" },
  SUCCESS:     { bg: "#f0fdf4", color: "#15803d" },
  FAILED:      { bg: "#fef2f2", color: "#b91c1c" },
  REFUNDED:    { bg: "#faf5ff", color: "#7c3aed" },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return <span style={{ color: "#94a3b8" }}>—</span>;
  const sc = STATUS_MAP[status] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span style={{ ...pill, background: sc.bg, color: sc.color }}>
      {status.replace(/_/g, " ")}
    </span>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "9px 0", borderBottom: "1px solid hsl(var(--border)/0.5)", gap: 12 }}>
    <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, flexShrink: 0, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))", textAlign: "right", wordBreak: "break-word" }}>{children}</span>
  </div>
);

const Section = ({ title, icon, children }: { title: string; icon?: string; children: React.ReactNode }) => (
  <div style={{
    background: "hsl(var(--card))", borderRadius: 12, padding: "16px 20px",
    border: "1px solid hsl(var(--border))", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: "hsl(var(--foreground))", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
      {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
      {title}
    </div>
    {children}
  </div>
);

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const inr = (n?: number) => n != null ? `₹${n.toLocaleString("en-IN")}` : "—";

const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({ open, booking, onClose, onRefundClick, onReimburseClick }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Profile Modal State
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileType, setProfileType] = useState<"user" | "scholar">("user");
  const [profileData, setProfileData] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const handleOpenUserProfile = async (userId?: string) => {
    if (!userId) return;
    setProfileType("user");
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    try {
      const data = await getUserProfile(userId);
      setProfileData(data);
    } catch (err) {
      console.error("Failed to load user profile", err);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleOpenScholarProfile = async (scholarId?: string) => {
    if (!scholarId) return;
    setProfileType("scholar");
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    try {
      const data = await getScholarProfile(scholarId);
      setProfileData(data);
    } catch (err) {
      console.error("Failed to load scholar profile", err);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !booking?.id) return;
    
    const fetchLogs = async () => {
      setLoadingLogs(true);
      try {
        const response = await API.get(`/bookings/${booking.id}/tracking-logs`);
        setLogs(response.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch tracking logs", err);
      } finally {
        setLoadingLogs(false);
      }
    };

    fetchLogs();
  }, [open, booking?.id]);

  if (!open || !booking) return null;

  return (
    <>
      <style>{`
        @keyframes bdmSlideUp {
          from { opacity: 0; transform: translateY(28px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .bdm-scroll::-webkit-scrollbar { width: 5px; }
        .bdm-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
      `}</style>
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={modal} onClick={(e) => e.stopPropagation()}>

          {/* ── Header ── */}
          <div style={{
            padding: "20px 24px 16px",
            borderBottom: "1px solid hsl(var(--border))",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexShrink: 0,
          }}>
            <div>
              <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, marginBottom: 4 }}>BOOKING ID</div>
              <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 16, color: "hsl(var(--foreground))" }}>
                #{booking.id?.toUpperCase()}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <StatusBadge status={booking.status} />
              <button onClick={onClose} style={{
                width: 34, height: 34, borderRadius: "50%",
                border: "1px solid hsl(var(--border))", background: "hsl(var(--card))",
                cursor: "pointer", fontSize: 16, color: "#94a3b8",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>✕</button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="bdm-scroll" style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, background: "hsl(var(--muted)/0.3)" }}>

            {/* Cancellation Overview Banner */}
            {booking.status === "CANCELLED" && (
              <div style={{
                background: "#fef2f2",
                border: "1.5px solid #fecaca",
                borderRadius: 14,
                padding: "16px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#991b1b", display: "flex", alignItems: "center", gap: 8 }}>
                    <span>❌</span> BOOKING CANCELLED
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12,
                    background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5"
                  }}>
                    Cancelled By: {booking.cancelledBy ? booking.cancelledBy.replace(/_/g, " ") : "USER / SYSTEM"}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#b91c1c", fontWeight: 600 }}>
                  Reason: <span style={{ fontWeight: 400 }}>{booking.cancelReason || "Scholar did not arrive / Cancelled by customer"}</span>
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4, paddingTop: 8, borderTop: "1px dashed #fca5a5", fontSize: 12 }}>
                  {booking.payment?.status === "REFUNDED" || booking.refunds?.length > 0 ? (
                    <span style={{ color: "#15803d", fontWeight: 700 }}>
                      ↩ Customer Refunded: {inr(booking.refunds?.[0]?.amount || booking.payment?.amount)}
                    </span>
                  ) : null}
                  {booking.penalties?.length > 0 ? (
                    <span style={{ color: "#b91c1c", fontWeight: 700 }}>
                      ⚠️ Scholar Penalty: {inr(booking.penalties[0].amount)} ({booking.penalties[0].status})
                    </span>
                  ) : null}
                  {booking.razorpayRefundId && (
                    <span style={{ color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>
                      Refund ID: {booking.razorpayRefundId}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Quick stats row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {[
                { label: "Total Price", value: inr(booking.price), color: "#2563eb" },
                { label: "Scholar Earned", value: inr(booking.scholarCharge), color: "#10b981" },
                { label: "Platform Fee", value: inr(booking.platformCharge), color: "#8b5cf6" },
              ].map((s, i) => (
                <div key={i} style={{
                  background: "hsl(var(--card))", borderRadius: 12, padding: "12px 16px",
                  border: "1px solid hsl(var(--border))",
                }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Booking Details */}
            <Section title="Booking Details" icon="📋">
              <Row label="Scheduled At">{fmt(booking.scheduledAt)}</Row>
              <Row label="Duration">{booking.durationMin ? `${booking.durationMin} minutes` : "—"}</Row>
              <Row label="Travel Expenses">{inr(booking.travelExpenses)}</Row>
              {booking.startPin ? (
                <Row label="Start Service PIN">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    🔑 <strong style={{ letterSpacing: 2, color: "#2563eb", fontSize: 14 }}>{booking.startPin}</strong>
                    {booking.status === "IN_PROGRESS" || booking.status === "COMPLETED" ? (
                      <span style={{ fontSize: 10, background: "#eff6ff", color: "#1d4ed8", padding: "1px 6px", borderRadius: 6, fontWeight: 700 }}>
                        VERIFIED / STARTED
                      </span>
                    ) : (
                      <span style={{ fontSize: 10, background: "#f1f5f9", color: "#64748b", padding: "1px 6px", borderRadius: 6 }}>
                        PENDING ENTRY
                      </span>
                    )}
                  </span>
                </Row>
              ) : null}
              {booking.completePin ? (
                <Row label="Completion PIN">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    🔐 <strong style={{ letterSpacing: 2, color: "#10b981", fontSize: 14 }}>{booking.completePin}</strong>
                    {booking.status === "COMPLETED" ? (
                      <span style={{ fontSize: 10, background: "#f0fdf4", color: "#15803d", padding: "1px 6px", borderRadius: 6, fontWeight: 700 }}>
                        VERIFIED / COMPLETED
                      </span>
                    ) : (
                      <span style={{ fontSize: 10, background: "#f1f5f9", color: "#64748b", padding: "1px 6px", borderRadius: 6 }}>
                        PENDING ENTRY
                      </span>
                    )}
                  </span>
                </Row>
              ) : null}
              <Row label="Service Address">{booking.serviceAddress || "—"}</Row>
              {booking.instructions && <Row label="Instructions">{booking.instructions}</Row>}
              <Row label="Created At">{fmt(booking.createdAt)}</Row>
              <Row label="Last Updated">{fmt(booking.updatedAt)}</Row>
            </Section>

            {/* Service */}
            <Section title="Service" icon="🕌">
              <Row label="Service Name">{booking.service?.name || "—"}</Row>
              <Row label="Religion">{booking.service?.religion || "—"}</Row>
            </Section>

            {/* People */}
            {(() => {
              const scholarRawId =
                booking.scholar?.id ||
                booking.scholarId ||
                booking.scholar?.userId ||
                booking.scholar?.user?.id;
              const scholarIdFormatted = formatScholarId(scholarRawId);

              const customerUserId =
                booking.customer?.user?.id ||
                booking.customer?.userId ||
                booking.customerId ||
                booking.customer?.id;

              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Section title="Customer" icon="👤">
                    <Row label="Name">
                      {booking.customer?.user?.name ? (
                        <span
                          onClick={() => handleOpenUserProfile(customerUserId)}
                          style={{
                            color: "#2563eb",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          title="View Customer Profile Modal"
                        >
                          {booking.customer.user.name} ↗
                        </span>
                      ) : (
                        "—"
                      )}
                    </Row>
                    <Row label="Phone">{booking.customer?.user?.phone || "—"}</Row>
                    <Row label="Email">{booking.customer?.user?.email || "—"}</Row>
                  </Section>
                  <Section title="Scholar" icon="🎓">
                    <Row label="Scholar ID">
                      {scholarRawId && scholarIdFormatted !== "—" ? (
                        <span
                          onClick={() => handleOpenScholarProfile(scholarRawId)}
                          style={{
                            color: "#2563eb",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 700,
                            fontFamily: "monospace",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          title="View Scholar Profile Modal"
                        >
                          {scholarIdFormatted} ↗
                        </span>
                      ) : (
                        "—"
                      )}
                    </Row>
                    <Row label="Name">
                      {booking.scholar?.user?.name ? (
                        <span
                          onClick={() => handleOpenScholarProfile(scholarRawId)}
                          style={{
                            color: "#2563eb",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          title="View Scholar Profile Modal"
                        >
                          {booking.scholar.user.name} ↗
                        </span>
                      ) : (
                        "—"
                      )}
                    </Row>
                    <Row label="Religion">{booking.scholar?.religion || "—"}</Row>
                  </Section>
                </div>
              );
            })()}

            {/* Payment */}
            <Section title="Payment" icon="💳">
              <Row label="Status">
                <StatusBadge status={booking.payment?.status} />
              </Row>
              <Row label="Amount">{inr(booking.payment?.amount || booking.price)}</Row>
              <Row label="Razorpay Order ID">{booking.payment?.razorpayOrderId || "—"}</Row>
              <Row label="Razorpay Payment ID">{booking.payment?.razorpayPaymentId || "—"}</Row>
              <Row label="Payment Date">{fmt(booking.payment?.createdAt)}</Row>
            </Section>

            {/* User Fare Breakup */}
            {(() => {
              const scholarSetPrice = booking.price || 0;
              const travelFee = booking.travelExpenses || 0;
              const additionalFee = booking.additionalAmount || 0;
              const platformFeeDeducted = booking.platformCharge || 0;
              const baseSubtotal = scholarSetPrice + travelFee + additionalFee;
              const totalPaidByCustomer = booking.payment?.amount || (baseSubtotal > 0 ? baseSubtotal : 0);
              const taxAndGatewayFees = Math.max(0, totalPaidByCustomer - baseSubtotal);

              return (
                <Section title="User Fare Breakup" icon="📊">
                  <Row label="Service Fee (Scholar Rate)">{inr(scholarSetPrice)}</Row>
                  <Row label="Travel Expenses">{inr(travelFee)}</Row>
                  {additionalFee > 0 && <Row label="Scholar Tip / Contribution">{inr(additionalFee)}</Row>}
                  {taxAndGatewayFees > 0 && (
                    <Row label="GST & Gateway Fee">{inr(taxAndGatewayFees)}</Row>
                  )}
                  <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", margin: "4px 0" }}>
                    * Note: Platform fee ({inr(platformFeeDeducted)}) is deducted directly from the scholar's set service fee.
                  </div>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0 0", marginTop: 6, borderTop: "2px solid hsl(var(--border))",
                    fontWeight: 800, fontSize: 13, color: "#1e40af"
                  }}>
                    <span>Total Paid by Customer</span>
                    <span>{inr(totalPaidByCustomer)}</span>
                  </div>
                </Section>
              );
            })()}

            {/* Scholar Earnings Breakup */}
            {(() => {
              const scholarSetPrice = booking.price || 0;
              const platformFeeDeducted = booking.platformCharge || 0;
              const scholarNetServiceFee = booking.scholarCharge != null 
              ? booking.scholarCharge 
              : Math.max(0, scholarSetPrice - platformFeeDeducted);
              const scholarTravelAllowance = booking.travelExpenses || 0;
              const scholarGrossEarnings = scholarNetServiceFee + scholarTravelAllowance;
              const activePenalties = booking.penalties || [];
              const totalPenaltiesAmount = activePenalties.reduce((sum: number, p: any) => p.status === 'UNSETTLED' ? sum + p.amount : sum, 0);
              const scholarPayouts = booking.payouts || [];
              const earningsPayout = scholarPayouts.find((p: any) => p.type === 'EARNINGS');
              const netScholarEarnings = earningsPayout?.amount ?? Math.max(0, scholarGrossEarnings - totalPenaltiesAmount);

              return (
                <Section title="Scholar Earnings & Deductions" icon="💰">
                  <Row label="Scholar Configured Service Price">{inr(scholarSetPrice)}</Row>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid hsl(var(--border)/0.5)", fontSize: 12 }}>
                    <span style={{ color: "#dc2626", fontWeight: 600 }}>Platform Fee (15% Commission)</span>
                    <span style={{ color: "#dc2626", fontWeight: 700 }}>- {inr(platformFeeDeducted)}</span>
                  </div>
                  <Row label="Scholar Net Service Fee">{inr(scholarNetServiceFee)}</Row>
                  <Row label="Travel Allowance">{inr(scholarTravelAllowance)}</Row>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "6px 0", fontSize: 12, fontWeight: 700, color: "hsl(var(--foreground))",
                    borderBottom: "1px solid hsl(var(--border)/0.5)"
                  }}>
                    <span>Gross Scholar Earnings</span>
                    <span>{inr(scholarGrossEarnings)}</span>
                  </div>

                  {activePenalties.length > 0 && (
                    <div style={{ marginTop: 8, padding: "8px 12px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fecaca" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", marginBottom: 4 }}>
                        Deductions & Penalties ({activePenalties.length})
                      </div>
                      {activePenalties.map((pen: any) => (
                        <div key={pen.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#dc2626", margin: "2px 0" }}>
                          <span>
                            {pen.reason} {pen.status === "WAIVED" ? "(Waived)" : `(${pen.status})`}
                          </span>
                          <span>- {inr(pen.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0 0", marginTop: 6, borderTop: "2px solid hsl(var(--border))",
                    fontWeight: 800, fontSize: 13, color: "#15803d"
                  }}>
                    <span>Net Scholar Payout / Earnings</span>
                    <span>{inr(netScholarEarnings)}</span>
                  </div>
                </Section>
              );
            })()}

            {/* Lifecycle & Tracking Logs Timeline */}
            <Section title="Booking Lifecycle & Service Activity Timeline" icon="⏱️">
              {loadingLogs && <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>Loading activity timeline...</div>}
              {!loadingLogs && logs.length === 0 && (
                <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0", fontStyle: "italic" }}>
                  No tracking logs recorded yet.
                </div>
              )}
              {!loadingLogs && logs.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
                  {logs.map((log: any, idx: number) => {
                    const dateObj = new Date(log.createdAt);
                    const timeStr = isNaN(dateObj.getTime()) ? "—" : dateObj.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                    const dateStr = isNaN(dateObj.getTime()) ? "" : dateObj.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
                    const hasCoords = log.lat != null && log.lng != null && (log.lat !== 0 || log.lng !== 0);
                    const mapUrl = hasCoords ? `https://www.google.com/maps/search/?api=1&query=${log.lat},${log.lng}` : null;
                    
                    const event = log.event || "EVENT";
                    let badgeBg = "#f1f5f9";
                    let badgeColor = "#475569";
                    let icon = "📌";

                    if (event === "BOOKING_CREATED") {
                      badgeBg = "#eff6ff"; badgeColor = "#1d4ed8"; icon = "🚀";
                    } else if (event === "SCHOLAR_ACCEPTED") {
                      badgeBg = "#f0fdf4"; badgeColor = "#15803d"; icon = "🤝";
                    } else if (event === "TRANSIT_STARTED") {
                      badgeBg = "#fdf4ff"; badgeColor = "#a21caf"; icon = "🚗";
                    } else if (event === "DELAY_REPORTED") {
                      badgeBg = "#fffbeb"; badgeColor = "#b45309"; icon = "⏱️";
                    } else if (event === "SERVICE_STARTED") {
                      badgeBg = "#eff6ff"; badgeColor = "#1e40af"; icon = "🔑";
                    } else if (event === "SERVICE_COMPLETED") {
                      badgeBg = "#f0fdf4"; badgeColor = "#166534"; icon = "🔐";
                    } else if (event === "BOOKING_CANCELLED") {
                      badgeBg = "#fef2f2"; badgeColor = "#991b1b"; icon = "❌";
                    } else if (event === "REFUND_PROCESSED") {
                      badgeBg = "#faf5ff"; badgeColor = "#6b21a8"; icon = "↩️";
                    } else if (event === "SCHOLAR_PENALTY_LEVIED") {
                      badgeBg = "#fff1f2"; badgeColor = "#be123c"; icon = "⚠️";
                    } else if (event === "SCHOLAR_REIMBURSED") {
                      badgeBg = "#f5f3ff"; badgeColor = "#5b21b6"; icon = "💸";
                    } else if (event === "LOCATION_UPDATE") {
                      badgeBg = "#f8fafc"; badgeColor = "#64748b"; icon = "📍";
                    }
                    
                    return (
                      <div key={log.id || idx} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                        padding: "10px 12px", background: "hsl(var(--muted)/0.2)", borderRadius: 10,
                        border: "1px solid hsl(var(--border)/0.6)", fontSize: 12, gap: 12
                      }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13 }}>{icon}</span>
                            <span style={{
                              fontWeight: 800, fontSize: 11, padding: "2px 7px", borderRadius: 6,
                              background: badgeBg, color: badgeColor, textTransform: "uppercase", letterSpacing: 0.3
                            }}>
                              {event.replace(/_/g, " ")}
                            </span>
                            {log.recordedBy && (
                              <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
                                by {log.recordedBy}
                              </span>
                            )}
                          </div>
                          {log.details && (
                            <span style={{ color: "hsl(var(--foreground))", fontSize: 12, lineHeight: 1.4, marginTop: 2 }}>
                              {log.details}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                          <span style={{ color: "#64748b", fontSize: 11, fontWeight: 600 }}>{timeStr}</span>
                          {dateStr && <span style={{ color: "#94a3b8", fontSize: 10 }}>{dateStr}</span>}
                          {mapUrl && (
                            <a href={mapUrl} target="_blank" rel="noreferrer" style={{
                              color: "#2563eb", textDecoration: "underline", fontWeight: 700, fontSize: 10, marginTop: 2
                            }}>
                              📍 {log.lat.toFixed(4)}, {log.lng.toFixed(4)}
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* Dispute section */}
            {(booking.status === "DISPUTED" || booking.disputeReason) && (
              <div style={{
                background: "#fff1f2", border: "1.5px solid #fecdd3", borderRadius: 12, padding: "16px 20px",
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#9f1239", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  ⚠️ Dispute Details
                </div>
                <Row label="Reason">{booking.disputeReason || "—"}</Row>
                {booking.disputeNotes && <Row label="Notes">{booking.disputeNotes}</Row>}
                {booking.disputeEvidence?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: "#9f1239", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>
                      Evidence ({booking.disputeEvidence.length} file{booking.disputeEvidence.length !== 1 ? "s" : ""})
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {booking.disputeEvidence.map((url: string, i: number) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" style={{
                          display: "inline-block", padding: "6px 14px", background: "#fecdd3",
                          color: "#9f1239", borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none",
                        }}>
                          📎 Evidence {i + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Customer Review & Rating */}
            {booking.review ? (
              <Section title="Customer Review & Rating" icon="⭐">
                <div style={{
                  background: "#fffbeb",
                  borderRadius: 10,
                  padding: "12px 16px",
                  border: "1px solid #fde68a",
                  marginBottom: 10,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 18, color: "#f59e0b", letterSpacing: 2 }}>
                        {"★".repeat(booking.review.rating)}{"☆".repeat(5 - booking.review.rating)}
                      </span>
                      <span style={{ fontWeight: 800, fontSize: 14, color: "#92400e" }}>
                        {booking.review.rating}.0 / 5.0
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: "#b45309", fontWeight: 600 }}>
                      {fmt(booking.review.createdAt)}
                    </span>
                  </div>
                  {booking.review.comment ? (
                    <div style={{ fontSize: 13, color: "#78350f", fontStyle: "italic", lineHeight: 1.5, marginTop: 4 }}>
                      "{booking.review.comment}"
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#92400e", fontStyle: "italic" }}>
                      Customer left a {booking.review.rating}-star rating without written remarks.
                    </div>
                  )}
                </div>
              </Section>
            ) : booking.status === "COMPLETED" ? (
              <Section title="Customer Review & Rating" icon="⭐">
                <div style={{ fontSize: 13, color: "#94a3b8", fontStyle: "italic", padding: "4px 0" }}>
                  No feedback submitted yet by the customer.
                </div>
              </Section>
            ) : null}

            {/* Payouts & Reimbursements */}
            {booking.payouts && booking.payouts.length > 0 && (
              <Section title="Scholar Payouts & Reimbursements" icon="💸">
                {booking.payouts.map((p: any) => (
                  <div key={p.id} style={{ padding: "8px 0", borderBottom: "1px solid hsl(var(--border)/0.3)", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: p.type === "REIMBURSEMENT" ? "#8b5cf6" : "#10b981" }}>
                        {p.type === "REIMBURSEMENT" ? "REIMBURSEMENT" : "EARNINGS PAYOUT"}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "hsl(var(--foreground))" }}>
                        {inr(p.amount)}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
                      <span>Status: <StatusBadge status={p.status} /></span>
                      <span>{fmt(p.createdAt)}</span>
                    </div>
                    {p.note && (
                      <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                        Note: {p.note}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      Processed By: {p.admin ? `${p.admin.name} (${p.admin.role.replace(/_/g, " ")})` : "System/Auto"}
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {/* User Refunds History */}
            {booking.refunds && booking.refunds.length > 0 && (
              <Section title="User Refunds History" icon="↩">
                {booking.refunds.map((r: any) => (
                  <div key={r.id} style={{ padding: "8px 0", borderBottom: "1px solid hsl(var(--border)/0.3)", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#ef4444" }}>
                        REFUNDED: {r.refundType.replace(/_/g, " ")}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "hsl(var(--foreground))" }}>
                        {inr(r.amount)}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
                      <span>Status: <StatusBadge status={r.status} /></span>
                      <span>{fmt(r.createdAt)}</span>
                    </div>
                    {r.reason && (
                      <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                        Reason: {r.reason}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      Processed By: {r.admin ? `${r.admin.name} (${r.admin.role.replace(/_/g, " ")})` : "System/Auto"}
                    </div>
                  </div>
                ))}
              </Section>
            )}
          </div>

          {/* ── Footer ── */}
          <div style={{
            padding: "14px 24px", borderTop: "1px solid hsl(var(--border))",
            display: "flex", justifyContent: "space-between", background: "hsl(var(--card))",
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {booking.payment && booking.payment.status !== "REFUNDED" && (
                <button
                  onClick={() => onRefundClick?.(booking)}
                  style={{
                    padding: "10px 16px", borderRadius: 12, border: "none",
                    background: "#ef4444", color: "#fff",
                    fontWeight: 700, cursor: "pointer", fontSize: 13,
                    boxShadow: "0 4px 12px rgba(239,68,68,0.2)"
                  }}>
                  ↩ Refund User
                </button>
              )}
              {booking.status === "CANCELLED" && !booking.payouts?.some((p: any) => p.type === "REIMBURSEMENT" && p.status === "PROCESSED") && (
                <button
                  onClick={() => onReimburseClick?.(booking)}
                  style={{
                    padding: "10px 16px", borderRadius: 12, border: "none",
                    background: "#8b5cf6", color: "#fff",
                    fontWeight: 700, cursor: "pointer", fontSize: 13,
                    boxShadow: "0 4px 12px rgba(139,92,246,0.2)"
                  }}>
                  💸 Reimburse Scholar
                </button>
              )}
            </div>
            <button onClick={onClose} style={{
              padding: "10px 28px", borderRadius: 12, border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))", color: "hsl(var(--muted-foreground))",
              fontWeight: 700, cursor: "pointer", fontSize: 14,
            }}>
              Close
            </button>
          </div>
        </div>
      </div>

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        data={profileData}
        loading={profileLoading}
        type={profileType}
      />
    </>
  );
};

export default BookingDetailsModal;
